<template>
  <div class="module">
    <router-link v-if="!isHostEmbed"
      :to="level === 'federal' ? '/classement' : `/classement/${encodeURIComponent(ligueCodeParam)}`"
      class="lb-back-link">
      <i class="fa-solid fa-arrow-left"></i> {{ level === 'federal' ? 'Tous les classements' : ligue ? ligue.name : 'Retour' }}
    </router-link>

    <div v-if="loadingEntity" class="lb-loading">Chargement...</div>
    <p v-else-if="!entity" class="lb-loading">{{ level === 'ligue' ? 'Ligue introuvable.' : 'Comité introuvable.' }}</p>

    <template v-else>
      <div class="scope-header">
        <img v-if="logoUrl(entity.logo)" :src="logoUrl(entity.logo)!" :alt="`Logo ${entity.name}`"
          class="entity-logo no-print" />
        <h1 class="page-title">Classement général {{ entity.name }} {{ selectedSeasonLabel }}</h1>
      </div>

      <!-- Visiteur anonyme : saison en cours uniquement, joueur sans code club/comité,
      points par tournoi -- sans filtres ni recherche. -->
      <template v-if="!authStore.isAuthenticated">
        <LoginPrompt message="Connectez-vous pour changer de saison, filtrer le classement, rechercher un joueur et voir les clubs." />

        <div class="mobile-rotate-notice">
          <span>📱</span> Tourne ton téléphone pour voir toutes les colonnes
        </div>

        <div v-if="loading" class="lb-loading">Chargement du classement...</div>
        <div v-else-if="error" class="error">{{ error }}</div>
        <div v-else-if="!hasRankingEntries(leaderboard)" class="lb-loading">
          Aucun classement disponible pour la saison {{ selectedSeasonLabel }}.
        </div>
        <PublicRankingTables v-else :categories="leaderboard" :columns="columns" />
      </template>

      <template v-else>
        <div class="filters">
          <label>Saison :</label>
          <select v-model="selectedSeasonId">
            <option v-for="season in seasons" :value="season.id" :key="season.id">{{ season.label }}</option>
          </select>

          <label>Catégorie :</label>
          <select v-model="selectedCategory">
            <option value="">Toutes</option>
            <option v-for="cat in leaderboard" :value="cat.category" :key="cat.category">
              {{ formatCategory(cat.category) }}
            </option>
          </select>

          <template v-if="level === 'federal'">
            <label>Ligue :</label>
            <select v-model="selectedLigue">
              <option value="">Toutes</option>
              <option v-for="code in uniqueLigues" :value="code" :key="code">{{ ligueName(code) }}</option>
            </select>
          </template>

          <label>{{ level === 'comite' ? 'Club' : 'Comité' }} :</label>
          <select v-model="selectedSubEntity">
            <option value="">Tous</option>
            <option v-for="code in uniqueSubEntities" :value="code" :key="code">{{ subEntityName(code) }}</option>
          </select>

          <input v-model="searchQuery" type="text" placeholder="Rechercher un joueur…" class="search-input" />

          <label class="checkbox-inline">
            <input v-model="onlyCurrentUser" type="checkbox" class="styled-checkbox" />
            Mes résultats uniquement
          </label>
        </div>

        <div v-if="canExport" class="lb-export-buttons no-print">
          <button :disabled="!hasRankingEntries(leaderboard)" @click="exportPdf">
            <i class="fa-solid fa-file-pdf"></i> Exporter en PDF
          </button>
          <button @click="showEmbed = !showEmbed">
            <i class="fa-solid fa-code"></i> Intégrer ce classement
          </button>
        </div>

        <!-- Code iframe à coller sur le site du comité / de la ligue (WordPress : bloc
        "HTML personnalisé") : version anonyme de la saison en cours, cf. LeaderboardEmbed. -->
        <div v-if="canExport && showEmbed" class="embed-panel no-print">
          <p>
            Copiez ce code et collez-le sur votre site (WordPress : bloc <strong>« HTML personnalisé »</strong>).
            Le classement affiché est la version publique de la saison en cours, mise à jour automatiquement,
            avec en bas de page la source et une invitation à se connecter pour voir le détail.
          </p>
          <div class="embed-theme">
            <span>Thème :</span>
            <label class="checkbox-inline">
              <input v-model="embedTheme" type="radio" value="sombre" /> Sombre
            </label>
            <label class="checkbox-inline">
              <input v-model="embedTheme" type="radio" value="clair" /> Clair
            </label>
          </div>
          <textarea :value="embedCode" readonly rows="10" @focus="($event.target as HTMLTextAreaElement).select()"></textarea>
          <div class="embed-panel-actions">
            <button @click="copyEmbedCode"><i class="fa-solid fa-copy"></i> Copier le code</button>
            <a :href="embedUrl" target="_blank" rel="noopener" class="cta-link">
              <i class="fa-solid fa-eye"></i> Aperçu
            </a>
          </div>
        </div>

        <div class="mobile-rotate-notice">
          <span>📱</span> Tourne ton téléphone pour voir toutes les colonnes
        </div>

        <div v-if="loading" class="lb-loading">Chargement du classement...</div>
        <div v-else-if="error" class="error">{{ error }}</div>
        <div v-else-if="!hasRankingEntries(leaderboard)" class="lb-loading">
          Aucun classement disponible pour la saison {{ selectedSeasonLabel }}.
        </div>

        <div v-else-if="!visibleCategories.length" class="lb-loading">Aucune entrée dans ce classement.</div>
        <div v-for="category in visibleCategories" v-else :key="category.category">
          <h3>{{ formatCategory(category.category) }}</h3>

          <table class="leaderboardtable">
            <colgroup>
              <col class="rank-col">
              <col class="player-col">
              <col class="pts-col">
              <template v-if="level === 'federal'">
                <col class="location-col hideonmobile">
                <col class="location-col hideonmobile">
              </template>
              <col v-for="col in columns" class="event-col hideonmobile" :key="col.key">
            </colgroup>
            <thead>
              <tr>
                <th></th>
                <th>Joueur</th>
                <th>PTS</th>
                <template v-if="level === 'federal'">
                  <th class="hideonmobile ptsdetails">Ligue</th>
                  <th class="hideonmobile ptsdetails">Comité</th>
                </template>
                <th v-for="col in columns" class="hideonmobile ptsdetails" :key="col.key">{{ col.label }}</th>
              </tr>
              <tr class="hideonmobile tournament-info">
                <th :colspan="level === 'federal' ? 5 : 3"></th>
                <th v-for="col in columns" :key="col.key">
                  <div class="city">{{ col.info.place || '' }}</div>
                  <div class="tournament-date">{{ formatShortDate(col.info.date) }}</div>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="entry in category.entries" :key="entry.joueur">
                <td><strong>{{ entry.clt || '-' }}</strong></td>
                <td :class="{ 'current-user': authStore.isCurrentUser_leaderboard(entry.joueur) }">{{ entry.joueur }}</td>
                <td><strong>{{ entry.pts || '0' }}</strong></td>
                <template v-if="level === 'federal'">
                  <td class="hideonmobile" :title="ligueName(entryLigue(entry))">{{ entryLigue(entry) }}</td>
                  <td class="hideonmobile" :title="subEntityName(entryComite(entry))">{{ entryComite(entry) }}</td>
                </template>
                <td v-for="col in columns" class="hideonmobile" :key="col.key">{{ entry[col.key] ?? '' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import backendApi from '@/axios/backendApi';
import { useAuthStore } from '@/stores/useAuthStore';
import { formatCategory } from '@/functions/utils';
import { applySeo } from '@/functions/seo';
import { toast } from 'vue3-toastify';
import { exportLeaderboardPdf } from '@/functions/leaderboardPdf';
import { app_url } from '@/config/config';
import { isHostEmbed } from '@/embedHost';
import {
  buildEmbedCode, canExportLeaderboard, extractEntityCode, fetchComites, fetchLigues, fetchSeasons, formatShortDate,
  hasRankingEntries, leaderboardSeo, logoUrl, rankingColumns, sameCode, type EmbedTheme, type Jurisdiction, type RankingCategory,
  type RankingEntry, type SeasonOption,
} from '@/functions/leaderboards';
import LoginPrompt from '@/components/LoginPrompt.vue';
import PublicRankingTables from '@/components/PublicRankingTables.vue';

// Classement général d'une ligue (/classement/:ligueCode/ligue), d'un comité
// (/classement/:ligueCode/:comiteCode) ou national FFD (/classement/national) -- même
// rendu que LeaderboardLigue/Comite/Federal de CMER, mais générique : n'importe quelle
// ligue/comité via /leaderboard/{niveau}/{id}/{saison}, /leaderboard/federal/{saison}.
const route = useRoute();
const authStore = useAuthStore();

const canExport = computed(() => canExportLeaderboard(authStore.scopes));
const ligueCodeParam = computed(() => String(route.params.ligueCode ?? ''));
const comiteCodeParam = computed(() => (route.params.comiteCode ? String(route.params.comiteCode) : ''));
const level = computed<'federal' | 'ligue' | 'comite'>(() => {
  if (route.name === 'LeaderboardFederal') return 'federal';
  return comiteCodeParam.value ? 'comite' : 'ligue';
});

const FEDERAL_ENTITY: Jurisdiction = { id: 0, code: 'FFD', name: 'National FFD', logo: null };

const ligue = ref<Jurisdiction | null>(null);
const entity = ref<Jurisdiction | null>(null);
const ligueComites = ref<Jurisdiction[]>([]);  // tous les comités au niveau fédéral
const loadingEntity = ref(true);

const seasons = ref<SeasonOption[]>([]);
const selectedSeasonId = ref<number | null>(null);
const selectedSeasonLabel = computed(() => seasons.value.find((s) => s.id === selectedSeasonId.value)?.label ?? '');

const leaderboard = ref<RankingCategory[]>([]);
const tournaments = ref<Record<string, { place: string; date: string }>>({});
const loading = ref(false);
const error = ref('');

const selectedCategory = ref('');
const selectedSubEntity = ref('');
const selectedLigue = ref('');  // fédéral uniquement, déduite du comité du joueur
const allLigues = ref<Jurisdiction[]>([]);
const onlyCurrentUser = ref(false);
const searchQuery = ref('');
// Noms des clubs DU comité affiché : short_code n'est pas unique entre comités (CDC,
// MDC, LFM...), le mapping global /clubs/config afficherait le club d'un autre comité.
const comiteClubNames = ref<Record<string, string>>({});

const normalize = (s: string) => s.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();

const columns = computed(() => rankingColumns(leaderboard.value, tournaments.value, level.value));

// Intégration sur un site tiers : même chemin préfixé par /embed (cf. router).
const showEmbed = ref(false);
const embedTheme = ref<EmbedTheme>('sombre');
const embedUrl = computed(() => `${app_url}/embed${route.path.replace(/\/+$/, '')}`
  + (embedTheme.value === 'clair' ? '?theme=clair' : ''));
const embedCode = computed(() => buildEmbedCode(embedUrl.value, `Classement général ${entity.value?.name ?? ''}`));
const copyEmbedCode = async () => {
  try {
    await navigator.clipboard.writeText(embedCode.value);
    toast.success("Code d'intégration copié.");
  } catch {
    toast.error('Copie impossible : sélectionnez le code et copiez-le manuellement.');
  }
};

// Au fédéral, le code entre parenthèses est celui du comité. Comité et ligue du joueur
// sont fournis par l'API (cf. routes.py::_federal_location) ; repli sur le code du nom et
// le référentiel des comités (ligue_code) si absents.
const ligueOfComite = (comiteCode: string): string =>
  ligueComites.value.find((c) => sameCode(c.code, comiteCode))?.ligue_code ?? '';
const ligueName = (code: string) => allLigues.value.find((l) => sameCode(l.code, code))?.name ?? code;
const entryComite = (e: RankingEntry): string => String(e.comite || extractEntityCode(e.joueur));
const entryLigue = (e: RankingEntry): string => String(e.ligue || ligueOfComite(entryComite(e)));
const matchesLigue = (e: RankingEntry) => !selectedLigue.value || entryLigue(e) === selectedLigue.value;

const uniqueLigues = computed(() => {
  const s = new Set<string>();
  for (const cat of leaderboard.value) for (const e of cat.entries) {
    const ligueCode = entryLigue(e);
    if (ligueCode) s.add(ligueCode);
  }
  return [...s].sort((a, b) => ligueName(a).localeCompare(ligueName(b)));
});

const uniqueSubEntities = computed(() => {
  const s = new Set<string>();
  for (const cat of leaderboard.value) for (const e of cat.entries) {
    const code = extractEntityCode(e.joueur);
    if (code && matchesLigue(e)) s.add(code);
  }
  return [...s].sort();
});

watch(selectedLigue, () => { selectedSubEntity.value = ''; });

const subEntityName = (code: string) => {
  if (level.value !== 'comite') return ligueComites.value.find((c) => sameCode(c.code, code))?.name ?? code;
  return comiteClubNames.value[code] ?? code;
};

const filteredCategories = computed(() => {
  const q = searchQuery.value.trim() ? normalize(searchQuery.value.trim()) : '';
  return leaderboard.value
    .filter((cat) => !selectedCategory.value || cat.category === selectedCategory.value)
    .map((cat) => ({
      ...cat,
      entries: cat.entries
        .filter((e) => !q || normalize(e.joueur).includes(q))
        .filter((e) => !selectedSubEntity.value || extractEntityCode(e.joueur) === selectedSubEntity.value)
        .filter((e) => matchesLigue(e))
        .filter((e) => !onlyCurrentUser.value || authStore.isCurrentUser_leaderboard(e.joueur)),
    }));
});

// Catégories vides (ex. "Mes résultats uniquement") masquées plutôt qu'affichées vides.
const visibleCategories = computed(() => filteredCategories.value.filter(cat => cat.entries.length > 0));

const fetchLeaderboard = async () => {
  if (!entity.value || selectedSeasonId.value == null) return;
  loading.value = true;
  error.value = '';
  try {
    const url = level.value === 'federal'
      ? `/leaderboard/federal/${selectedSeasonId.value}`
      : `/leaderboard/${level.value}/${entity.value.id}/${selectedSeasonId.value}`;
    const { data } = await backendApi.get(url);
    leaderboard.value = data.leaderboard || [];
    tournaments.value = data.tournaments || {};
  } catch (err: any) {
    error.value = err.response?.data?.detail || 'Échec du chargement du classement.';
    leaderboard.value = [];
    tournaments.value = {};
  } finally {
    loading.value = false;
  }
};

const exportPdf = () => {
  if (!entity.value) return;
  const federal = level.value === 'federal';
  const filters = [
    selectedCategory.value && formatCategory(selectedCategory.value),
    selectedLigue.value && ligueName(selectedLigue.value),
    selectedSubEntity.value && subEntityName(selectedSubEntity.value),
  ].filter(Boolean).join(' - ');
  exportLeaderboardPdf({
    title: `Classement général ${entity.value.name} ${selectedSeasonLabel.value}`,
    subtitle: filters || undefined,
    head: [
      '#', 'Joueur', 'PTS', ...(federal ? ['Ligue', 'Comité'] : []),
      ...columns.value.map((c) => c.label.replace('\n', ' ')),
    ],
    subHead: columns.value.map((c) => [c.info.place, formatShortDate(c.info.date)].filter(Boolean).join('\n')),
    subHeadOffset: federal ? 5 : 3,
    categories: filteredCategories.value.map((cat) => ({
      name: formatCategory(cat.category),
      rows: cat.entries.map((e) => [
        e.clt || '-', e.joueur, e.pts || '0', ...(federal ? [entryLigue(e), entryComite(e)] : []),
        ...columns.value.map((c) => e[c.key] ?? ''),
      ]),
    })),
    filename: `classement-${entity.value.code.toLowerCase()}-${selectedSeasonLabel.value}.pdf`,
  });
};

const currentSeasonId = () => seasons.value.find((s) => s.is_current)?.id ?? seasons.value[0]?.id ?? null;

watch(selectedSeasonId, fetchLeaderboard);
// Déconnexion : filtres masqués pour un anonyme, ils ne doivent plus s'appliquer (retour
// à la saison en cours, seule visible sans compte).
watch(() => authStore.isAuthenticated, (auth) => {
  if (auth) return;
  selectedCategory.value = '';
  selectedLigue.value = '';
  selectedSubEntity.value = '';
  searchQuery.value = '';
  onlyCurrentUser.value = false;
  selectedSeasonId.value = currentSeasonId();
});

onMounted(async () => {
  try {
    const [ligues, allSeasons] = await Promise.all([fetchLigues(), fetchSeasons()]);
    seasons.value = allSeasons;
    allLigues.value = ligues;
    if (level.value === 'federal') {
      entity.value = FEDERAL_ENTITY;
      ligueComites.value = await fetchComites();
    }
    ligue.value = ligues.find((l) => sameCode(l.code, ligueCodeParam.value)) ?? null;
    if (ligue.value && level.value !== 'federal') {
      ligueComites.value = await fetchComites(ligue.value.code);
      entity.value = level.value === 'ligue'
        ? ligue.value
        : ligueComites.value.find((c) => sameCode(c.code, comiteCodeParam.value)) ?? null;
    }
    if (entity.value) {
      const season = seasons.value.find((s) => s.id === currentSeasonId())?.label ?? '';
      applySeo(leaderboardSeo(level.value, season, ligue.value, entity.value));
    }
    if (level.value === 'comite' && entity.value) {
      backendApi.get('/clubs/config', { params: { comite_code: entity.value.code } })
        .then(({ data }) => { comiteClubNames.value = data.clubMapping ?? {}; })
        .catch(() => { /* repli : code brut */ });
    }
  } catch {
    entity.value = null;
  } finally {
    loadingEntity.value = false;
  }
  // Déclenche fetchLeaderboard via le watch.
  selectedSeasonId.value = currentSeasonId();
});
</script>

<style scoped>
.embed-panel {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  max-width: 900px;
  margin: 0 auto 1.5rem;
  padding: 1rem 1.25rem;
  border: 1px solid var(--color-light-shadow, #ddd);
  border-radius: var(--radius, 8px);
  background: var(--color-bg-lighter, #f5f5f5);
}

.embed-panel p {
  margin: 0;
  font-size: 0.9rem;
}

.embed-theme {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.5rem 1.25rem;
  font-weight: 600;
}

.embed-panel textarea {
  width: 100%;
  box-sizing: border-box;
  font-family: monospace;
  font-size: 0.8rem;
  resize: vertical;
}

.embed-panel-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 0.75rem;
}

.scope-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}
</style>
